import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { resolveRouteSeo, GLOBAL_KEYWORDS } from './routeMeta';
import { absoluteUrl, getDefaultOgImageUrl, SITE_CONFIG } from './siteConfig';
import { buildPageJsonLd } from './jsonLd';

/**
 * Динамические meta, canonical, Open Graph и JSON-LD по текущему маршруту.
 */
export function SeoHead() {
    const { pathname } = useLocation();
    const seo = useMemo(() => resolveRouteSeo(pathname), [pathname]);

    const canonical = absoluteUrl(pathname === '/' ? '/' : pathname);
    const ogImage = seo.ogImagePath ? absoluteUrl(seo.ogImagePath) : getDefaultOgImageUrl();
    const jsonLdBlocks = useMemo(
        () => buildPageJsonLd(seo.jsonLd || []),
        [seo.jsonLd]
    );

    const keywords = seo.keywords
        ? `${seo.keywords}, ${GLOBAL_KEYWORDS}`
        : GLOBAL_KEYWORDS;

    return (
        <Helmet>
            <html lang={SITE_CONFIG.defaultLanguage} />
            <title>{seo.title}</title>
            <meta name="description" content={seo.description} />
            <meta name="keywords" content={keywords} />
            <meta name="author" content={SITE_CONFIG.siteName} />
            <meta name="robots" content={seo.robots || 'index, follow'} />
            <link rel="canonical" href={canonical} />

            {/* Географическое таргетирование — Казахстан, Алматы */}
            <meta name="geo.region" content="KZ-ALA" />
            <meta name="geo.placename" content="Алматы, Казахстан" />
            <meta name="geo.position" content="43.238293;76.945465" />
            <meta name="ICBM" content="43.238293, 76.945465" />

            {/* hreflang — сайт на русском для Казахстана */}
            <link rel="alternate" hrefLang="ru-KZ" href={canonical} />
            <link rel="alternate" hrefLang="ru" href={canonical} />
            <link rel="alternate" hrefLang="x-default" href={canonical} />

            {/* Open Graph */}
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={SITE_CONFIG.siteName} />
            <meta property="og:locale" content={SITE_CONFIG.defaultLocale} />
            <meta property="og:url" content={canonical} />
            <meta property="og:title" content={seo.title} />
            <meta property="og:description" content={seo.description} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content={`${SITE_CONFIG.siteName} - 3D моделирование и подбор оборудования для бассейна в Казахстане`} />

            {/* Twitter / X Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={canonical} />
            <meta name="twitter:title" content={seo.title} />
            <meta name="twitter:description" content={seo.description} />
            <meta name="twitter:image" content={ogImage} />
            <meta name="twitter:image:alt" content={`${SITE_CONFIG.siteName} - подбор оборудования для бассейна`} />

            {jsonLdBlocks.map((block, index) => (
                <script key={`jsonld-${index}`} type="application/ld+json">
                    {JSON.stringify(block)}
                </script>
            ))}
        </Helmet>
    );
}
